import React, { useState, useEffect } from 'react';
import './NotificationPrompt.css';

const NotificationPrompt = ({ forceOpen, onForceClose }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [permission, setPermission] = useState('default');

  const isVisible = forceOpen || showPrompt;

  const closePrompt = () => {
    setShowPrompt(false);
    if (onForceClose) onForceClose();
  };

  useEffect(() => {
    // Check if user has already made a decision
    const notificationDecision = localStorage.getItem('notificationDecision');
    const visitCount = parseInt(localStorage.getItem('visitCount') || '0', 10);
    
    // Increment visit count
    localStorage.setItem('visitCount', (visitCount + 1).toString());

    // Show prompt on second visit if no decision has been made
    // This follows best practices - not annoying users on first visit
    if (!notificationDecision && visitCount >= 1 && 'Notification' in window) {
      // Delay showing prompt by 3 seconds for better UX
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    }

    // Update permission state
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const handleEnableNotifications = async () => {
    try {
      if (!('Notification' in window)) {
        alert('This browser does not support notifications');
        return;
      }

      if (!('serviceWorker' in navigator)) {
        alert('This browser does not support service workers');
        return;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission === 'granted') {
        // Register service worker and subscribe to push notifications
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        
        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;

        // Get VAPID public key from server
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${apiBase}/notifications/public-key`);
        const { publicKey } = await response.json();

        // Subscribe to push notifications
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        });

        // Send subscription to server
        await fetch(`${apiBase}/notifications/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(subscription)
        });

        localStorage.setItem('notificationDecision', 'granted');
        closePrompt();
        
        // Show success notification
        new Notification('Health Notifications Enabled! 🏥', {
          body: 'You will now receive updates about new health articles and wellness tips.',
          icon: '/logo192.png'
        });
      } else {
        localStorage.setItem('notificationDecision', 'denied');
        closePrompt();
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      alert('Failed to enable notifications. Please try again.');
    }
  };

  const handleNotNow = () => {
    // Don't save decision, will ask again on next visit
    closePrompt();
  };

  const handleNeverAsk = () => {
    localStorage.setItem('notificationDecision', 'never');
    closePrompt();
  };

  // Helper function to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="notification-prompt-overlay">
      <div className="notification-prompt">
        <div className="notification-prompt-icon">
          🔔
        </div>
        
        <h2 className="notification-prompt-title">Stay Healthy, Stay Informed</h2>
        
        <p className="notification-prompt-description">
          Get updates on important health articles, wellness tips, and medical insights delivered to you. 
          Never miss crucial health information again.
        </p>
        
        <div className="notification-prompt-actions">
          <button 
            className="notification-prompt-button notification-prompt-button-primary"
            onClick={handleEnableNotifications}
          >
            Enable notifications
          </button>
          
          <button 
            className="notification-prompt-button notification-prompt-button-secondary"
            onClick={handleNeverAsk}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPrompt;
